"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useClickOutside } from "modules/common/hooks/useClickOutside";

interface UseDropdownMenuProps {
  menuWidth?: number;
  menuHeight?: number;
  padding?: number;
}

export function useDropdownMenu({
  menuWidth = 208,
  menuHeight = 300,
  padding = 8,
}: UseDropdownMenuProps = {}) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    buttonRef.current?.focus({ preventScroll: true });
  }, []);

  const dismissMenu = useCallback(() => {
    setShowMenu(false);
  }, []);

  // The portaled menu and its trigger do not share a DOM parent. Treat the
  // trigger as part of the dropdown so its mousedown cannot close the menu
  // immediately before the click handler tries to toggle it.
  const handleClickOutside = useCallback(
    (event?: MouseEvent) => {
      const target = event?.target;
      if (target instanceof Node && buttonRef.current?.contains(target)) {
        return;
      }
      dismissMenu();
    },
    [dismissMenu]
  );

  useClickOutside(menuRef, handleClickOutside, showMenu);

  // Calculate menu position relative to viewport
  const calculateMenuPosition = useCallback(
    (itemCount?: number) => {
      if (!buttonRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const actualMenuHeight = itemCount ? itemCount * 40 + 32 : menuHeight;

      let top = buttonRect.bottom + padding - 20;
      let left = buttonRect.right - menuWidth + 20; // Align to right edge

      // Adjust if menu would go off-screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Horizontal bounds check
      if (left + menuWidth > viewportWidth - padding) {
        left = viewportWidth - menuWidth - padding;
      }
      if (left < padding) {
        left = padding;
      }

      // Vertical bounds check
      if (top + actualMenuHeight > viewportHeight - padding) {
        top = buttonRect.top - actualMenuHeight - padding;
      }

      if (top < padding) {
        top = padding;
      }

      setMenuPos({ top, left });
    },
    [menuWidth, menuHeight, padding]
  );

  const handleButtonClick = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      itemCount?: number
    ) => {
      e.preventDefault();
      e.stopPropagation();

      if (!showMenu) {
        calculateMenuPosition(itemCount);
      }

      setShowMenu((current) => !current);
    },
    [showMenu, calculateMenuPosition]
  );

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        return;
      }

      if (
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Home" &&
        event.key !== "End"
      ) {
        return;
      }

      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])'
        ) ?? []
      );
      if (items.length === 0) return;

      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);
      const nextIndex =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? items.length - 1
            : event.key === "ArrowUp"
              ? currentIndex <= 0
                ? items.length - 1
                : currentIndex - 1
              : currentIndex < 0 || currentIndex === items.length - 1
                ? 0
                : currentIndex + 1;
      items[nextIndex]?.focus({ preventScroll: true });
    },
    [closeMenu]
  );

  useEffect(() => {
    if (!showMenu) return;

    const frame = window.requestAnimationFrame(() => {
      menuRef.current
        ?.querySelector<HTMLElement>(
          '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])'
        )
        ?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [showMenu]);

  // A fixed, portaled menu should not float away from the card that opened it.
  // Dismiss on viewport or nested-container movement instead of chasing it.
  useEffect(() => {
    if (!showMenu) return;

    window.addEventListener("scroll", dismissMenu, true);
    window.addEventListener("resize", dismissMenu);

    return () => {
      window.removeEventListener("scroll", dismissMenu, true);
      window.removeEventListener("resize", dismissMenu);
    };
  }, [showMenu, dismissMenu]);

  return {
    showMenu,
    menuPos,
    menuRef,
    buttonRef,
    handleButtonClick,
    handleMenuKeyDown,
    closeMenu,
    setShowMenu,
  };
}
